/**
 * A BinaryTree class
 * @module
 */

const BINARY_TREE_T_THRESHOLD = 1e-6;

export type BinaryTreeValue<T> = (data: T) => number;
export type BinaryTreeFilter<T> = (data: T) => boolean;

export enum BinaryTreeIterator {
  INCREASING,
  DECREASING,
}

export class BinaryTreeNode<T> {
  data: T | undefined;
  toValue: BinaryTreeValue<T>;

  count: number;

  allowDuplicates: boolean;

  private left: BinaryTreeNode<T> | null = null;
  private right: BinaryTreeNode<T> | null = null;

  constructor(
    toValue: BinaryTreeValue<T>,
    data?: T,
    allowDuplicates: boolean = false,
    private readonly tolerance: number = BINARY_TREE_T_THRESHOLD,
  ) {
    if (data) {
      this.data = data;
    }
    this.toValue = toValue;
    this.count = data ? 1 : 0;
    this.allowDuplicates = allowDuplicates;
  }

  // check if this data exists in the tree
  has(data: T) {
    return this.find(data) !== null;
  }

  find(data: T): T | null {
    if (!this.data) {
      return null;
    } else if (this.isEqual(data, this.data)) {
      return this.data;
    } else {
      const compare = this.toValue(data) - this.toValue(this.data);
      if (compare <= 0) {
        if (this.left) {
          return this.left.find(data);
        } else {
          return null;
        }
      } else {
        if (this.right) {
          return this.right.find(data);
        } else {
          return null;
        }
      }
    }
  }

  /**
   * Add data to the tree sorted by t
   * @param data
   * @param overrideExisting if an inserted node and an existing node are equal this callback defines which one is kept
   * @param mutateExisting an optional callback for combining data between the inserted node and an existing equal node
   * If `overrideExisting` is also defined, this callback is called afterwards
   * @returns
   */
  insert(
    data: T,
    overrideExisting: (currData: T, newData: T) => boolean = () => false,
    mutateExisting?: (currData: T, newData: T, overridePred: boolean) => T,
  ): boolean {
    if (!this.data) {
      this.data = data;
      this.count++;
      return true;
    } else if (this.isEqual(data, this.data) && !this.allowDuplicates) {
      const res: boolean = overrideExisting(this.data, data);
      if (res) {
        this.data = mutateExisting ? mutateExisting(this.data, data, true) : data;
        return true;
      } else {
        if (mutateExisting) {
          this.data = mutateExisting(this.data, data, false);
        }
        return false;
      }
    } else {
      const compare = this.toValue(data) - this.toValue(this.data);
      if (compare < 0 || (this.allowDuplicates && compare === 0)) {
        // add this data to the left if smaller
        if (this.left) {
          this.count -= this.left.count;
          const res = this.left.insert(data, overrideExisting, mutateExisting);
          this.count += this.left.count;
          return res;
        } else {
          this.left = new BinaryTreeNode(this.toValue, data, this.allowDuplicates, this.tolerance);
          this.count++;
          return true;
        }
      } else if (compare > 0) {
        // add this data to the right if larger
        if (this.right) {
          this.count -= this.right.count;
          const res = this.right.insert(data, overrideExisting, mutateExisting);
          this.count += this.right.count;
          return res;
        } else {
          this.right = new BinaryTreeNode(this.toValue, data, this.allowDuplicates, this.tolerance);
          this.count++;
          return true;
        }
      } else {
        // data.t === this.data.t
        // this is a duplicate point => do not add
        return false;
      }
    }
  }

  private isEqual(a: T, b: T) {
    return Math.abs(this.toValue(a) - this.toValue(b)) <= this.tolerance;
  }

  // push all data in this node to target sorted by t
  // this could probably be a Generator<DimensionTreeData> and save the target array allocation
  retrieve(
    filter?: BinaryTreeFilter<T>,
    target: T[] = [],
    start = -Number.MAX_VALUE,
    startInclusive = false,
    end = Number.MAX_VALUE,
    endInclusive = false,
  ) {
    let vAbove: boolean = false,
      vBelow: boolean = false;

    if (this.data) {
      const v = this.toValue(this.data);
      vAbove = v > start || (v === start && startInclusive);
      vBelow = v < end || (v === end && endInclusive);
    }

    // all data with t < this.data.t
    if (this.left && vAbove) {
      this.left.retrieve(filter, target, start, startInclusive, end, endInclusive);
    }
    // this data if applicable
    if (this.data && vAbove && vBelow && (!filter || filter(this.data))) {
      target.push(this.data);
    }
    if (this.right && vBelow) {
      // all data with t > this.data.t
      this.right.retrieve(filter, target, start, startInclusive, end, endInclusive);
    }

    return target;
  }

  forEach(iterator: BinaryTreeIterator, callback: (data: T) => void, filter?: BinaryTreeFilter<T>) {
    if (iterator === BinaryTreeIterator.INCREASING) {
      // all data with t < this.data.t
      if (this.left) {
        this.left.forEach(iterator, callback, filter);
      }
    } else {
      // all data with t > this.data.t
      if (this.right) {
        this.right.forEach(iterator, callback, filter);
      }
    }

    // this data if applicable
    if (this.data && (!filter || filter(this.data))) {
      callback(this.data);
    }

    if (iterator === BinaryTreeIterator.INCREASING) {
      // all data with t < this.data.t
      if (this.right) {
        this.right.forEach(iterator, callback, filter);
      }
    } else {
      // all data with t > this.data.t
      if (this.left) {
        this.left.forEach(iterator, callback, filter);
      }
    }
  }

  // get the first item in the set
  first(filter: BinaryTreeFilter<T> = () => true): T | null {
    let data: T | null = null;

    if (this.left) {
      data = this.left.first(filter);
    }
    if (!data && this.data && filter(this.data)) {
      data = this.data;
    }
    if (!data && this.right) {
      data = this.right.first(filter);
    }

    return data;
  }

  // get the last item in the set
  last(filter: BinaryTreeFilter<T> = () => true): T | null {
    let data: T | null = null;

    if (this.right) {
      data = this.right.last(filter);
    }
    if (!data && this.data && filter(this.data)) {
      data = this.data;
    }
    if (!data && this.left) {
      data = this.left.last(filter);
    }

    return data;
  }
}
